<script setup lang="ts">
import { computed, watch } from 'vue';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { useTranslation } from '@/utils/locales/messages';
import { useDatePicker } from '@/shared/composables/useDatePicker';
import { formatDateByJurisdiction } from '@/features/register/utils/dateFormat';

const props = defineProps<{
  modelValue: string;
  label: string;
  readonly?: boolean;
  required?: boolean;
}>();

const emit = defineEmits<{ 'update:modelValue': [value: string] }>();

const languageStore = useLanguageStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

const requiredRule = computed(() =>
  props.required ? [(v: string) => !!v?.trim() || t('setupEntity.sgIncorporation.validation.required')] : undefined,
);

const externalValue = computed(() => props.modelValue);
const { dateValue, menuOpen, calendarDate } = useDatePicker(externalValue, false);

const displayValue = computed(() => formatDateByJurisdiction(dateValue.value));

watch(dateValue, (value) => {
  if (value !== props.modelValue) {
    emit('update:modelValue', value);
  }
});

function handleDateUpdate(val: Date | string) {
  let dateStr: string;
  if (val instanceof Date) {
    const year = val.getFullYear();
    const month = String(val.getMonth() + 1).padStart(2, '0');
    const day = String(val.getDate()).padStart(2, '0');
    dateStr = `${year}-${month}-${day}`;
  } else {
    dateStr = val;
  }
  dateValue.value = dateStr;
  calendarDate.value = dateStr;
  menuOpen.value = false;
  emit('update:modelValue', dateStr);
}
</script>

<template>
  <v-menu
    v-if="!readonly"
    v-model="menuOpen"
    :close-on-content-click="false"
    location="bottom"
  >
    <template #activator="{ props: activatorProps }">
      <v-text-field
        :model-value="displayValue"
        :label="label"
        :rules="requiredRule"
        variant="outlined"
        density="comfortable"
        placeholder="dd/mm/yyyy"
        hide-details
        prepend-inner-icon="mdi-calendar"
        readonly
        v-bind="activatorProps"
      />
    </template>
    <v-date-picker
      :model-value="calendarDate ? new Date(`${calendarDate}T00:00:00`) : new Date()"
      @update:model-value="handleDateUpdate"
    />
  </v-menu>
  <v-text-field
    v-else
    :model-value="displayValue"
    :label="label"
    variant="filled"
    density="comfortable"
    placeholder="dd/mm/yyyy"
    hide-details
    prepend-inner-icon="mdi-calendar"
    readonly
  />
</template>
